import axiosIntents from '@/utils/axios-intents'

import StoreCache from '@/utils/StoreCache.js'

const DEFAULT_LIFETIME_USAGE = 1 * 60 * 60 * 1000 // 1 hour

export default {
    state: {
        unified_collections_list: new StoreCache(DEFAULT_LIFETIME_USAGE),
        unified_articles_list: new StoreCache(DEFAULT_LIFETIME_USAGE),
        unified_articles_search: {
            searches: {},
            lifetime: 10 * 60 * 1000, // 10 minutes
        },
    },

    actions: {
        GetUnifiedCollectionsList({ state, commit }) {
            const collections_list = state.unified_collections_list

            if (!collections_list.expired()) {
                return Promise.resolve(collections_list.cachedData())
            }

            return axiosIntents.get('/support/help/collections').then(({data}) => {
                const collections = (data?.Collections || []).map(({ ID, Title, Description }) => ({
                    id: ID,
                    name: Title,
                    description: Description,
                }))

                commit('SAVE_UNIFIED_COLLECTIONS_LIST', collections)

                return collections
            })
            .catch(error => {
                console.error('GetUnifiedCollectionsList error', error)

                return Promise.resolve([])
            })
        },

        GetUnifiedAllArticles({ state, commit, dispatch }) {
            const articles_list = state.unified_articles_list

            if (!articles_list.expired()) {
                return Promise.resolve(articles_list.cachedData())
            }

            return dispatch('GetUnifiedCollectionsList').then(collections => {
                const requests = collections.map(collection =>
                    axiosIntents.get(`/support/help/collections/${collection.id}/articles`)
                        .then(({data}) => (data?.Articles || []).map(article => ({
                            id: article.ID,
                            title: article.Title,
                            body: article.Body,
                            category_id: article.CollectionID || collection.id,
                            section_id: article.CollectionID || collection.id,
                            label_names: [],
                        })))
                        .catch(() => [])
                )

                return Promise.all(requests).then(results => {
                    const articles = results.flat()

                    commit('SAVE_UNIFIED_ARTICLES_LIST', articles)

                    return articles
                })
            })
            .catch(error => {
                console.error('GetUnifiedAllArticles error', error)

                return Promise.resolve([])
            })
        },

        GetUnifiedArticlesByCollectionId({ dispatch }, collection_id) {
            return dispatch('GetUnifiedAllArticles').then(articles => {
                return articles.filter(article => article.section_id === collection_id)
            })
        },

        // Expires collections and articles cache, forcing a re-fetch on next call
        // From a component: this.$store.dispatch('ExpireUnifiedHelpCache')
        // From browser console: document.querySelector('#app').__vue__.$store.dispatch('ExpireUnifiedHelpCache')
        ExpireUnifiedHelpCache({ state }) {
            state.unified_collections_list.expireNow()
            state.unified_articles_list.expireNow()
        },

        FindUnifiedArticles({ state, commit }, search_word) {
            const search_cache = state.unified_articles_search.searches[search_word]

            if (search_cache && !search_cache.expired()) {
                return Promise.resolve(search_cache.cachedData())
            }

            return axiosIntents.get(`/support/help/articles/search?query=${encodeURIComponent(search_word)}`).then(({data}) => {
                const search_articles_ids = (data?.Articles || []).map(({ID}) => ID)

                commit('SAVE_UNIFIED_ARTICLES_SEARCH', { search_word, search_articles_ids })

                return search_articles_ids
            }).catch(error => {
                console.error('FindUnifiedArticles error', error)

                return Promise.resolve([])
            })
        },
    },

    mutations: {
        SAVE_UNIFIED_COLLECTIONS_LIST(state, collections_list) {
            state.unified_collections_list.fix(collections_list)
        },

        SAVE_UNIFIED_ARTICLES_LIST(state, articles_list) {
            state.unified_articles_list.fix(articles_list)
        },

        SAVE_UNIFIED_ARTICLES_SEARCH(state, { search_word, search_articles_ids }) {
            if (!state.unified_articles_search.searches[search_word]) {
                state.unified_articles_search.searches[search_word] = new StoreCache(state.unified_articles_search.lifetime)
            }

            state.unified_articles_search.searches[search_word].fix(search_articles_ids)
        },
    },
}
